import { useState } from "react";
import {
  Box, Typography, Paper, Button, Stack, Grid2 as Grid, TextField, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Divider, CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  purchaseOrdersApi, purchaseOrderStateApi, branchesApi,
  type ItemDto, type VendorDto,
  type CreatePurchaseOrderRequest,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { ItemPicker, VendorPicker } from "@/components/Pickers";

interface DraftLine {
  key: string;
  item: ItemDto | null;
  qty: number;
  rate: number;
  taxAmount: number;
}

const newLine = (): DraftLine => ({
  key: crypto.randomUUID(), item: null, qty: 1, rate: 0, taxAmount: 0,
});

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

export function CreatePurchaseOrderPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canSubmit = useAuth(s => s.hasPermission("procurement.po.submit"));

  const [vendor, setVendor] = useState<VendorDto | null>(null);
  const [warehouseId, setWarehouseId] = useState<string>("");
  const [documentDate, setDocumentDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState<string>("");
  const [vendorRef, setVendorRef] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [lines, setLines] = useState<DraftLine[]>([newLine()]);

  const branches = useQuery({
    queryKey: ["branches"], queryFn: () => branchesApi.list(), staleTime: 5 * 60_000,
  });

  const updateLine = (key: string, patch: Partial<DraftLine>) =>
    setLines((ls) => ls.map((l) => {
      if (l.key !== key) return l;
      const next = { ...l, ...patch };
      // Changing the item refills the rate from its default MRP (even if a rate was already set).
      if ("item" in patch) next.rate = patch.item?.defaultMrp ?? 0;
      // Auto-calculate GST from the item's tax rate whenever item / qty / rate change.
      if ("item" in patch || "qty" in patch || "rate" in patch)
        next.taxAmount = Math.round(next.qty * next.rate * (next.item?.gstRate ?? 0)) / 100;
      return next;
    }));
  const removeLine = (key: string) => setLines((ls) => ls.filter((l) => l.key !== key));
  const addLine = () => setLines((ls) => [...ls, newLine()]);

  const subTotal = lines.reduce((s, l) => s + l.qty * l.rate, 0);
  const taxTotal = lines.reduce((s, l) => s + l.taxAmount, 0);
  const grandTotal = subTotal + taxTotal;

  const validate = (): string | null => {
    if (!vendor) return "Pick a vendor";
    if (!warehouseId) return "Pick a destination warehouse";
    if (lines.length === 0) return "Add at least one line";
    for (const [i, l] of lines.entries()) {
      if (!l.item) return `Line ${i + 1}: item not selected`;
      if (l.qty <= 0) return `Line ${i + 1}: qty must be > 0`;
      if (l.rate < 0) return `Line ${i + 1}: rate must be >= 0`;
    }
    return null;
  };

  const mutation = useMutation({
    meta: { successMessage: "Purchase order created" },
    mutationFn: async (opts: { submit: boolean }) => {
      const payload: CreatePurchaseOrderRequest = {
        vendorId: vendor!.id,
        warehouseId,
        documentDate,
        expectedDeliveryDate: expectedDeliveryDate || undefined,
        vendorRef: vendorRef || undefined,
        notes: notes || undefined,
        lines: lines.map((l) => ({
          itemId: l.item!.id, qty: l.qty, rate: l.rate, taxAmount: l.taxAmount,
        })),
      };
      const created = await purchaseOrdersApi.create(payload);
      if (opts.submit) {
        // Fetch the fresh rowVersion, then submit with If-Match (S1 concurrency).
        const detail = await purchaseOrdersApi.get(created.id);
        await purchaseOrderStateApi.submit(created.id, detail.rowVersion);
      }
      return created;
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
      navigate(`/procurement/purchase-orders/${created.id}`);
    },
  });

  const validationError = validate();

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>New purchase order</Typography>
      <Paper sx={{ p: 3 }}>
        {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 5 }}>
            <VendorPicker value={vendor} onChange={setVendor} label="Vendor" required />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField select SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} label="Destination warehouse *"
              value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}
              size="small" fullWidth>
              <option value="">— Pick branch —</option>
              {branches.data?.map((b) => <option key={b.id} value={b.id}>{b.code} — {b.name}</option>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField label="Document date *" type="date" size="small" fullWidth
              value={documentDate} onChange={(e) => setDocumentDate(e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField label="Expected delivery" type="date" size="small" fullWidth
              value={expectedDeliveryDate} onChange={(e) => setExpectedDeliveryDate(e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField label="Vendor reference" size="small" fullWidth
              value={vendorRef} onChange={(e) => setVendorRef(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField label="Notes" size="small" fullWidth multiline rows={1}
              value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Lines</Typography>
          <Button startIcon={<AddIcon />} onClick={addLine} size="small">Add line</Button>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 240 }}>Item *</TableCell>
              <TableCell align="right" sx={{ width: 100 }}>Qty *</TableCell>
              <TableCell align="right" sx={{ width: 120 }}>Rate (₹) *</TableCell>
              <TableCell align="right" sx={{ width: 120 }}>Tax (₹)</TableCell>
              <TableCell align="right" sx={{ width: 140 }}>Line total</TableCell>
              <TableCell sx={{ width: 50 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((l, idx) => {
              const lineTotal = l.qty * l.rate + l.taxAmount;
              return (
                <TableRow key={l.key}>
                  <TableCell>
                    <ItemPicker value={l.item} onChange={(v) => updateLine(l.key, { item: v })}
                      label={`Line ${idx + 1}`} required error={!l.item} />
                  </TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" value={l.qty}
                      onChange={(e) => updateLine(l.key, { qty: Number(e.target.value) || 0 })}
                      inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }}
                      sx={{ width: 90 }} />
                  </TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" value={l.rate}
                      onChange={(e) => updateLine(l.key, { rate: Number(e.target.value) || 0 })}
                      inputProps={{ min: 0, step: "0.0001", style: { textAlign: "right" } }}
                      sx={{ width: 110 }} />
                  </TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" value={l.taxAmount}
                      onChange={(e) => updateLine(l.key, { taxAmount: Number(e.target.value) || 0 })}
                      inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }}
                      sx={{ width: 110 }} />
                  </TableCell>
                  <TableCell align="right">{inr.format(lineTotal)}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => removeLine(l.key)} disabled={lines.length === 1}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow>
              <TableCell colSpan={3} />
              <TableCell align="right"><strong>Subtotal</strong></TableCell>
              <TableCell align="right">{inr.format(subTotal)}</TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell colSpan={3} />
              <TableCell align="right"><strong>Tax</strong></TableCell>
              <TableCell align="right">{inr.format(taxTotal)}</TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell colSpan={3} />
              <TableCell align="right"><strong>Grand total</strong></TableCell>
              <TableCell align="right"><strong>{inr.format(grandTotal)}</strong></TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>

        <Divider sx={{ my: 3 }} />
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button onClick={() => navigate("/procurement/purchase-orders")}>Cancel</Button>
          <Button variant="outlined"
            disabled={!!validationError || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={() => mutation.mutate({ submit: false })}>
            Create draft
          </Button>
          {canSubmit && (
            <Button variant="contained" color="success"
              disabled={!!validationError || mutation.isPending}
              startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              onClick={() => mutation.mutate({ submit: true })}>
              Create &amp; submit
            </Button>
          )}
        </Stack>
        {validationError && <Alert severity="info" sx={{ mt: 2 }}>{validationError}</Alert>}
      </Paper>
    </Box>
  );
}
